import { Head, Link } from "@inertiajs/react"
import { FiArrowLeft, FiMapPin } from "react-icons/fi"

type Card = {
  slug: string
  title: string
  subtitle: string | null
  description: string | null
  public_url: string
  logo_radius: number
  logo_url: string | null
  cover_image_url: string | null
}

type Props = {
  category_label: string
  cards: Card[]
  canonical_url: string
}

export default function CategoryIndex({ category_label, cards, canonical_url }: Props) {
  const title = `${category_label} | כרטיסי ביקור דיגיטליים | Cardly`
  const description = `כרטיסי ביקור דיגיטליים של ${category_label} ב-Cardly. מצאו עסק, שירות או בעל מקצוע ושלחו הודעה בקלות.`
  const structuredData = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description,
    url: canonical_url,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: cards.map((card, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: card.public_url,
        name: card.title,
      })),
    },
  }).replace(/</g, "\\u003c")

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical_url} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical_url} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: structuredData }} />
      </Head>
      <main className="min-h-screen bg-slate-50 px-4 py-8 text-slate-900 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <Link href="/" className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:text-brand-800"><FiArrowLeft aria-hidden="true" />Cardly</Link>
          <div className="mt-10 max-w-2xl">
            <p className="text-sm font-semibold text-brand-700">כרטיסי ביקור דיגיטליים</p>
            <h1 className="mt-2 text-3xl font-bold sm:text-4xl">{category_label}</h1>
            <p className="mt-3 text-lg leading-8 text-slate-600">מצאו עסקים ובעלי מקצוע בתחום {category_label}, עם כל הפרטים החשובים במקום אחד.</p>
          </div>

          {cards.length === 0 ? (
            <div className="glass-card mt-8 p-8 text-center text-slate-600">עדיין אין כרטיסים זמינים בקטגוריה הזו.</div>
          ) : (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map((card) => (
                <Link key={card.slug} href={card.public_url} className="glass-card group overflow-hidden transition hover:-translate-y-0.5 hover:shadow-xl">
                  <div className="h-36 bg-gradient-to-br from-brand-100 to-slate-100">
                    {card.cover_image_url ? <img src={card.cover_image_url} alt="" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden bg-brand-50 text-brand-700" style={{ borderRadius: card.logo_radius === 999 ? "50%" : `${card.logo_radius ?? 24}px` }}>
                        {card.logo_url ? <img src={card.logo_url} alt="" className="h-full w-full object-cover" style={{ borderRadius: card.logo_radius === 999 ? "50%" : `${card.logo_radius ?? 24}px` }} /> : <FiMapPin aria-hidden="true" />}
                      </div>
                      <h2 className="font-semibold group-hover:text-brand-700">{card.title}</h2>
                    </div>
                    {card.subtitle ? <p className="mt-3 line-clamp-2 text-sm text-slate-600">{card.subtitle}</p> : null}
                    {card.description ? <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-500">{card.description}</p> : null}
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand-700">לצפייה בכרטיס<FiArrowLeft aria-hidden="true" /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  )
}
